import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RequireAuth } from '@/components/guards/RequireAuth';
import { LoginPage } from '@/pages/LoginPage';
import { MagicLinkVerifyPage } from '@/pages/MagicLinkVerifyPage';
import { MyDayPage } from '@/pages/MyDayPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { EmployeesPage } from '@/pages/EmployeesPage';
import { EmployeeProfilePage } from '@/pages/EmployeeProfilePage';
import { AccountabilityChartPage } from '@/pages/AccountabilityChartPage';
import { ScorecardsPage } from '@/pages/ScorecardsPage';
import { OvertimeTrackerPage } from '@/pages/admin/OvertimeTrackerPage';
import { RocksPage } from '@/pages/eos/RocksPage';
import { TodosPage } from '@/pages/eos/TodosPage';
import { IssuesPage } from '@/pages/eos/IssuesPage';
import { MeetingsPage } from '@/pages/eos/MeetingsPage';
import { MeetingRunnerPage } from '@/pages/eos/MeetingRunnerPage';
import { VtoPage } from '@/pages/eos/VtoPage';
import { PeopleAnalyzerPage } from '@/pages/eos/PeopleAnalyzerPage';
import { OneOnOnesPage } from '@/pages/eos/OneOnOnesPage';
import { DelegateElevatePage } from '@/pages/eos/DelegateElevatePage';
import { MyTrainingPage } from '@/pages/training/MyTrainingPage';
import { CoursePlayerPage } from '@/pages/training/CoursePlayerPage';
import { QuizTakePage } from '@/pages/training/QuizTakePage';
import { CertificatePage } from '@/pages/training/CertificatePage';
import { CourseListPage } from '@/pages/admin/training/CourseListPage';
import { CourseBuilderPage } from '@/pages/admin/training/CourseBuilderPage';
import { AssignmentsPage } from '@/pages/admin/training/AssignmentsPage';
import { TrainingReportsPage } from '@/pages/admin/training/TrainingReportsPage';
import { ChecklistsPage } from '@/pages/admin/training/ChecklistsPage';
import { CsQueuePage } from '@/pages/cs/CsQueuePage';
import { CsTicketDetailPage } from '@/pages/cs/CsTicketDetailPage';
import { CsOperationsPage } from '@/pages/cs/CsOperationsPage';
import { CommunicationPage } from '@/pages/comms/CommunicationPage';
import { MessagesPage } from '@/pages/comms/MessagesPage';
import { CustomerProfitabilityPage } from '@/pages/admin/shipedge/CustomerProfitabilityPage';
import { LotLookupPage } from '@/pages/admin/shipedge/LotLookupPage';
import { OperationalMixPage } from '@/pages/admin/shipedge/OperationalMixPage';
import { SyncLogPage } from '@/pages/admin/shipedge/SyncLogPage';
import { AlertsSettingsPage } from '@/pages/settings/AlertsSettingsPage';
import { ProfileSettingsPage } from '@/pages/settings/ProfileSettingsPage';
import { DepartmentsPage } from '@/pages/settings/DepartmentsPage';
import { IntegrationsPage } from '@/pages/settings/IntegrationsPage';
import { IntegrationScaffolderPage } from '@/pages/settings/IntegrationScaffolderPage';
import { InvestigationsSettingsPage } from '@/pages/settings/InvestigationsSettingsPage';
import { InsightsPage } from '@/pages/insights/InsightsPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { DocumentDetailPage } from '@/pages/documents/DocumentDetailPage';
import { ClientsPage } from '@/pages/clients/ClientsPage';
import { ClientDetailPage } from '@/pages/clients/ClientDetailPage';
import { SopListPage } from '@/pages/compliance/SopListPage';
import { SopDetailPage } from '@/pages/compliance/SopDetailPage';
import { SopAuditIndexPage } from '@/pages/compliance/SopAuditIndexPage';
import { AuditLogPage } from '@/pages/compliance/AuditLogPage';
import { AuditorSeatsPage } from '@/pages/compliance/AuditorSeatsPage';

/**
 * Top-level router. Public routes (login, verify) are listed first, then the
 * authenticated AppShell wraps everything that requires a session. RequireAuth
 * redirects to /login if the user isn't signed in and shows a brief loading
 * state while the session is being hydrated from /api/auth/me.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/authenticate" element={<MagicLinkVerifyPage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<MyDayPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/employees/:id" element={<EmployeeProfilePage />} />
          <Route path="/accountability" element={<AccountabilityChartPage />} />
          <Route path="/scorecards" element={<ScorecardsPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/eos/rocks" element={<RocksPage />} />
          <Route path="/eos/todos" element={<TodosPage />} />
          <Route path="/eos/issues" element={<IssuesPage />} />
          <Route path="/eos/meetings" element={<MeetingsPage />} />
          <Route path="/eos/meetings/:id/run" element={<MeetingRunnerPage />} />
          <Route path="/eos/vto" element={<VtoPage />} />
          <Route path="/eos/people" element={<PeopleAnalyzerPage />} />
          <Route path="/eos/one-on-ones" element={<OneOnOnesPage />} />
          <Route path="/eos/delegate-elevate" element={<DelegateElevatePage />} />
          <Route path="/training" element={<MyTrainingPage />} />
          <Route path="/training/courses/:courseId" element={<CoursePlayerPage />} />
          <Route path="/training/courses/:courseId/quiz/:quizId" element={<QuizTakePage />} />
          <Route path="/training/certificates/:code" element={<CertificatePage />} />
          <Route path="/admin/training/courses" element={<CourseListPage />} />
          <Route path="/admin/training/courses/:id" element={<CourseBuilderPage />} />
          <Route path="/admin/training/assignments" element={<AssignmentsPage />} />
          <Route path="/admin/training/reports" element={<TrainingReportsPage />} />
          <Route path="/admin/training/checklists" element={<ChecklistsPage />} />
          {/* Customer Service (Phase 16 / ADR-026). Operations tabs are
            URL-driven via ?tab= — one route covers the whole hub. */}
          <Route path="/cs" element={<CsQueuePage />} />
          <Route path="/cs/tickets/:ticketId" element={<CsTicketDetailPage />} />
          <Route path="/cs/operations" element={<CsOperationsPage />} />
          {/* Communication (Stream E / ADR-027). Messages is one component
            for the list + a deep-linked thread. */}
          <Route path="/communication" element={<CommunicationPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:conversationId" element={<MessagesPage />} />
          <Route path="/settings/departments" element={<DepartmentsPage />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />
          <Route path="/settings/integrations/scaffold" element={<IntegrationScaffolderPage />} />
          <Route path="/settings/alerts" element={<AlertsSettingsPage />} />
          <Route path="/settings/investigations" element={<InvestigationsSettingsPage />} />
          <Route path="/settings/profile" element={<ProfileSettingsPage />} />
          <Route path="/admin/overtime" element={<OvertimeTrackerPage />} />
          <Route path="/admin/shipedge/customer-profitability" element={<CustomerProfitabilityPage />} />
          <Route path="/admin/shipedge/operational-mix" element={<OperationalMixPage />} />
          <Route path="/admin/shipedge/lot-lookup" element={<LotLookupPage />} />
          <Route path="/admin/shipedge/sync-log" element={<SyncLogPage />} />
          {/* Audit-facing master index (auditor-readonly seats land here). Static
            path declared BEFORE `:slug` so it isn't shadowed by the param. */}
          <Route path="/compliance/audit-index" element={<SopAuditIndexPage />} />
          <Route path="/compliance/audit-log" element={<AuditLogPage />} />
          <Route path="/compliance/auditor-seats" element={<AuditorSeatsPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:id" element={<DocumentDetailPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/compliance/sops" element={<SopListPage />} />
          <Route path="/compliance/sops/:slug" element={<SopDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
